import { GetStaticProps } from "next";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/blog.module.scss";
import "../../styles/scss/resources.module.scss";

import parse from "html-react-parser";
import Link from "next/link";
import Layout from "../../components/layout";
import CallToActionBlogBottom from "../../components/v2/call-to-action-blog-bottom";
import CardWithIcon from "../../components/v2/cards/card-with-icon";
import {
  CustomerStoryHexa,
  EventHexa,
  WhitepaperHexa,
} from "../../components/v2/icons";
import SearchBlog from "../../components/v2/blog/search-blog";
import { getProductUpdatesPosts } from "../../lib/api";
import { mwImage } from "../../lib/common";

const INITIAL_VISIBLE = 4;
const LOAD_MORE_COUNT = 4;

export default function ProductUpdates({ allProductUpdates }) {
  const { t } = useTranslation("common");
  const belowPost = allProductUpdates.edges;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const visiblePosts = belowPost.slice(0, visibleCount);
  const hasMore = visibleCount < belowPost.length;

  useEffect(() => {
    if (!hasMore) return;

    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((prev) =>
            Math.min(prev + LOAD_MORE_COUNT, belowPost.length)
          );
        }
      },
      { rootMargin: "200px 0px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, belowPost.length, visibleCount]);

  return (
    <Layout
      isHomePage={false}
      pageTitle={t("productUpdates.meta.title")}
      ogImage={"/images/resources/product-update-og.png"}
      metaTitle={t("productUpdates.meta.title")}
      metaDesc={t("productUpdates.meta.description")}
      uri={"/product-updates/"}
      metaRobotsNoindex={"index"}
      metaRobotsNofollow={"follow"}
    >
      <div className="middle-wrapper site-page blog-v2 product-updates-page">
        <section className="product-updates-hero">
          <div className="container">
            <div className="product-updates-hero-content">
              <div className="product-updates-hero-copy">
                <h1>
                  Middleware {t("productUpdates.hero.title")}
                </h1>
                <p>{t("productUpdates.hero.description")}</p>
              </div>
              <div className="product-updates-search">
                <SearchBlog
                  searchInputPostDetails={belowPost}
                  parentSlug="product-updates"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="product-update-blog">
          <div className="container">
            <div className="product-update-timeline">
              {visiblePosts.map((__post) => {
                const post = __post.node;
                const title = post.title;
                return (
                  <article className="product-update-row" key={post.uri}>
                    <div className="product-update-date">
                      {post?.acf_productUpdate?.publishDate}
                    </div>
                    <div className="product-update-entry">
                      <div className="product-update-img">
                        <Link
                          href={`/product-updates/${post.slug}/`}
                          itemProp="url"
                        >
                          <Image
                            src={mwImage(post?.featuredImage?.node?.sourceUrl)}
                            alt={post?.featuredImage?.node?.altText || title}
                            width={520}
                            height={278}
                            loading="lazy"
                            unoptimized
                            sizes="(max-width: 767px) 100vw, 520px"
                          />
                        </Link>
                      </div>
                      <div className="product-update-content">
                        <div
                          className="post-list-item"
                          itemScope
                          itemType="http://schema.org/Article"
                        >
                          <Link
                            href={`/product-updates/${post.slug}/`}
                            itemProp="url"
                          >
                            <h2>{parse(title)}</h2>
                          </Link>
                          <div className="product-update-excerpt">
                            {parse(post?.excerpt || "")}
                          </div>
                          <Link
                            href={`/product-updates/${post.slug}/`}
                            className="read-more"
                          >
                            Read More
                          </Link>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
            {hasMore ? (
              <div
                ref={loadMoreRef}
                className="product-update-load-more"
                aria-hidden="true"
              />
            ) : null}
          </div>
        </section>

        <section className="mw-card-with-icon blog-page">
          <div className="container">
            <div className="card-items">
              <div className="d-flex card-items">
                <CardWithIcon
                  icon={<WhitepaperHexa />}
                  title={t("blog.cards.whitepaper.title")}
                  description={t("blog.cards.whitepaper.description")}
                  link={"/whitepaper/"}
                />
                <CardWithIcon
                  icon={<CustomerStoryHexa />}
                  title={t("blog.cards.customerStories.title")}
                  description={t("blog.cards.customerStories.description")}
                  link={"/customers/"}
                />
                <CardWithIcon
                  icon={<EventHexa />}
                  title={t("productUpdates.cards.events.title")}
                  description={t("productUpdates.cards.events.description")}
                  link={"/events/"}
                />
              </div>
            </div>
          </div>
        </section>

        <CallToActionBlogBottom
          img={"/images/resources/middleware-resource-team.png"}
        />
      </div>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async ({
  preview = false,
  locale,
}) => {
  const allProductUpdates = await getProductUpdatesPosts();
  const translations = await serverSideTranslations(
    locale ?? "en",
    ["common", "header", "footer"],
    nextI18NextConfig,
  );

  return {
    props: {
      ...translations,
      allProductUpdates: allProductUpdates,
      pageTitle: "Middleware Product Updates | Latest Features & Enhancements",
      ogImage: "/images/resources/product-update-og.png",
      metaTitle: "Middleware Product Updates | Latest Features & Enhancements",
      metaDesc:
        "Stay updated with Middleware's latest product updates, new integrations, and feature enhancements. Discover how our full-stack observability platform evolves to improve monitoring, performance, and scalability.",
      description:
        "Stay updated with Middleware's latest product updates, new integrations, and feature enhancements. Discover how our full-stack observability platform evolves to improve monitoring, performance, and scalability.",
      uri: "/product-updates/",
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
    },
  };
};
